import Box from '@mui/material/Box'
import { downloadCsvFile } from 'api/csv'
import AssignUserDialog from 'components/AppDialogs/AssignUserDialog'
import CsvConfigDialog from 'components/AppDialogs/CsvConfigDialog'
import DelDocDialog from 'components/AppDialogs/DelDocDialog'
import RenameDocDialog from 'components/AppDialogs/RenameDocDialog'
import SendDocDialog from 'components/AppDialogs/SendDocDialog'
import ControlArea from 'components/ListPage/HomeControlArea'
import HomeUploadBox from 'components/ListPage/HomeUploadBox'
import FullHeightFlexContainer from 'components/ui/FullHeightFlexContainer'
import UploadDialogContainer from 'components/UploadDialog/UploadDialogContainer'
import { DOC_TYPE, DEFAULT_PER_PAGE, MODE } from 'config'
import { useAlert } from 'hooks/useDialog'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useHomeLoaderData } from './Home.loader'
import DocumentsList from './ListPage/DocumentsList'

export default function Home() {
  const [_searchParams, setSearchParams] = useSearchParams()
  const [selectedIDs, setSelectedIDs] = useState<string[]>([])
  const [openAssignDialog, setOpenAssignDialog] = useState(false)
  const [openDelDialog, setOpenDelDialog] = useState(false)
  const [openSendDialog, setOpenSendDialog] = useState(false)
  const [renameDialog, setRenameDialog] = useState({ open: false, objectID: '', name: '' })
  const [openCsvConfigDialog, setOpenCsvConfigDialog] = useState(false)
  const [openUploadDialog, setOpenUploadDialog] = useState(false)
  const alert = useAlert()
  const setOpenRenameDialog = (x: boolean) => setRenameDialog((s) => ({ ...s, open: x }))
  const openRenameDialog = (objectID: string, name: string) =>
    setRenameDialog({ open: true, objectID, name })

  const ldata = useHomeLoaderData() // as { documents: DocListItem[], total: number, params?: any } || undefined
  const { documents, total, params } = ldata
  const page = parseInt(params?.page) || 0
  const perPage = parseInt(params?.perPage) || DEFAULT_PER_PAGE

  const selectedDocs = useMemo(() => {
    if (selectedIDs.length === 0) return []
    return (documents || []).filter((doc) => selectedIDs.includes(doc.objectID))
  }, [selectedIDs, documents])

  const reloadDocs = useCallback(() => {
    setSearchParams((s) => s)
  }, [setSearchParams])

  useEffect(() => {
    setSelectedIDs(
      (sIDs) => documents?.map((d) => d.objectID).filter((id) => sIDs.includes(id)) || [],
    )
  }, [documents])

  return (
    <FullHeightFlexContainer>
      <h2>文書一覧</h2>
      <ControlArea
        selectedDocs={selectedDocs}
        selectedIDs={selectedIDs}
        setOpenAssignDialog={setOpenAssignDialog}
        setOpenDelDialog={setOpenDelDialog}
        setOpenSendDialog={setOpenSendDialog}
        setOpenCsvConfigDialog={setOpenCsvConfigDialog}
        setOpenUploadDialog={setOpenUploadDialog}
        reloadDocs={reloadDocs}
      />

      <Box display="flex" flexWrap="nowrap" overflow="hidden">
        <Box flexGrow={4} minWidth={0}>
          <DocumentsList
            selectedIDs={selectedIDs}
            setSelectedIDs={setSelectedIDs}
            openRenameDialog={openRenameDialog}
            documents={documents}
            page={page}
            perPage={perPage}
            total={total}
          />
        </Box>
        <Box pl={2} minWidth={200} maxWidth={280} flexGrow={1}>
          <HomeUploadBox setOpen={setOpenUploadDialog} />
        </Box>
      </Box>
      <UploadDialogContainer
        mode={MODE.NORMAL}
        open={openUploadDialog}
        setOpen={setOpenUploadDialog}
      />
      {documents && (
        <>
          <AssignUserDialog
            open={openAssignDialog}
            setOpenDialog={setOpenAssignDialog}
            objectIDs={selectedIDs}
            documents={documents}
            reloadDocs={reloadDocs}
          />
          <DelDocDialog
            open={openDelDialog}
            setOpenDialog={setOpenDelDialog}
            selectedIDs={selectedIDs}
            documents={documents}
            reloadDocs={(ids: string[]) => ids.length > 0 && reloadDocs()}
          />
          <SendDocDialog
            open={openSendDialog}
            setOpenDialog={setOpenSendDialog}
            selectedIDs={selectedIDs}
            setSelectedIDs={setSelectedIDs}
            reload={reloadDocs}
            fromList
          />
          <RenameDocDialog
            key={renameDialog.objectID}
            open={renameDialog.open}
            setOpenDialog={setOpenRenameDialog}
            objectID={renameDialog.objectID}
            afterUpdated={(_newName: string) => reloadDocs()}
            name={renameDialog.name}
          />
          <CsvConfigDialog
            docType={selectedDocs[0]?.documentType || DOC_TYPE.INVOICE}
            docTypeName={selectedDocs[0]?.documentTypeName}
            ocrBuilderConfigID={selectedDocs[0]?.ocrBuilderConfigID}
            open={openCsvConfigDialog}
            actionText="出力"
            onClose={async (canceled: boolean) => {
              setOpenCsvConfigDialog(false)
              if (canceled || selectedDocs.length === 0) return // 念のためlength check
              try {
                await downloadCsvFile(selectedIDs)
              } catch {
                alert({ message: 'ダウンロードに失敗しました。' })
              }
              reloadDocs()
            }}
          />
        </>
      )}
    </FullHeightFlexContainer>
  )
}
